import React from "react";
import { ServiceDetails } from ".";
import ServiceDetailsStore from "./ServiceDetailsStore";
import { observer, inject } from "mobx-react";
import classNames from "classnames";
import {
  validateVIN,
  validateRegistration,
} from "../../../js/utils/CarDetailsValidator";
import Loader from "../Loader";
import { InputField } from "./components";

interface ServicePricingFormProps {}

interface ServicePricingFormState {
  regoNumber?: string;
  regoNumberError?: boolean;
  regoNumberErrorString?: string;
  vinNumber?: string;
  vinNumberError?: boolean;
  vinNumberErrorString?: string;
  submitDisabled?: boolean;
  selectedGrpVarId: string;
  isVerified: boolean;
  recaptchaServerFailed: boolean;
}

interface InjectedProps extends ServicePricingFormProps {
  serviceDetailsStore: ServiceDetailsStore;
}

@inject("serviceDetailsStore")
@observer
class ServicePricingForm extends React.Component<
  ServicePricingFormProps,
  ServicePricingFormState
> {
  constructor(props: ServicePricingFormProps) {
    super(props);
    this.state = {
      regoNumber: "",
      regoNumberErrorString: "",
      regoNumberError: false,
      vinNumber: "",
      vinNumberErrorString: "",
      vinNumberError: false,
      submitDisabled: true,
      selectedGrpVarId: "",
      isVerified: false,
      recaptchaServerFailed: false,
    };
    window["validateRecallRecaptcha"] = this.validateRecaptcha;
    window["recaptchaExpired"] = this.recaptchaHasExpired;
  }

  componentDidMount(): void {
    window["pushDigitalData"]({
      event: "_pageLoaded",
    });
    window["pushDigitalData"](
      {
        event: "_formNavigate",
        form: {
          name: "service pricing for owners",
          stage: "service pricing for owners",
        },
      },
      true
    );
  }

  get injected() {
    return this.props as InjectedProps;
  }

  submitForm = (e) => {
    if (!this.state.regoNumberError && !this.state.vinNumberError) {
      if (this.state.isVerified) {
        window["pushDigitalData"](
          {
            event: "_formNavigate",
            form: {
              name: "service pricing for owners",
              stage: "submitted",
            },
          },
          true
        );
        const { serviceDetailsStore } = this.injected;
        serviceDetailsStore.operationDetails.setDetailsExist(false);
        e.preventDefault();
        serviceDetailsStore.operationDetails.fetchVehicleServiceDetails(
          serviceDetailsStore.vehicleMetadata.vin,
          this.state.selectedGrpVarId
        );
      } else {
        this.setState({
          recaptchaServerFailed: true,
        });
      }
    }
  };

  handleRegoChange = ({ target: { value } }) => {
    const isError = validateRegistration(value);
    this.setState({
      regoNumberError: isError !== "",
      regoNumberErrorString: isError,
      regoNumber: !isError ? value : "",
      vinNumber: this.state.vinNumberError ? "" : this.state.vinNumber,
      vinNumberError: false,
    });
  };

  handleVINChange = ({ target: { value } }) => {
    const isError = validateVIN(value);
    this.setState({
      vinNumberError: isError !== "",
      vinNumberErrorString: isError,
      vinNumber: !isError ? value : "",
      regoNumber: this.state.regoNumberError ? "" : this.state.regoNumber,
      regoNumberError: false,
    });
  };

  handleIntervalChange = ({ target: { value } }) => {
    this.setState({
      selectedGrpVarId: value,
      submitDisabled: false,
    });
  };

  handleRegoBlur = () => {
    const { serviceDetailsStore } = this.injected;
    serviceDetailsStore.operationDetails.setDetailsExist(false);
    if (!this.state.regoNumberError) {
      serviceDetailsStore.vehicleMetadata.fetchVehicleDetailsByRego(
        this.state.regoNumber
      );
      this.setState({
        selectedGrpVarId: "",
        submitDisabled: true,
      });
    } else {
      serviceDetailsStore.resetDetails();
    }
  };

  handleVinBlur = () => {
    const { serviceDetailsStore } = this.injected;
    serviceDetailsStore.operationDetails.setDetailsExist(false);
    if (!this.state.vinNumberError) {
      const { serviceDetailsStore } = this.injected;
      serviceDetailsStore.vehicleMetadata.fetchVehicleDetailsByVin(
        this.state.vinNumber
      );
      this.setState({
        selectedGrpVarId: "",
        submitDisabled: true,
      });
    } else {
      serviceDetailsStore.resetDetails();
    }
  };

  handleRegoKeyUp = (target) => {
    if (target.keyCode === 13) {
      this.handleRegoBlur();
    }
  };

  handleVinKeyUp = (target) => {
    if (target.keyCode === 13) {
      this.handleVinBlur();
    }
  };
  validateRecaptcha = (response) => {
    if (response != null) {
      this.setState({
        isVerified: true,
      });
    }
  };
  recaptchaHasExpired = (response) => {
    if (response == undefined) {
      this.setState({
        isVerified: false,
      });
    }
  };

  render() {
    const { recaptchaServerFailed } = this.state;
    const { serviceDetailsStore } = this.injected;
    const regoInputClass = classNames({
      text: true,
      textInputRego: true,
      pc__input: true,
      "pc__input--isLoading":
        serviceDetailsStore.vehicleMetadata.isRegoFetching,
      "input-validation-error":
        this.state.regoNumberError ||
        serviceDetailsStore.vehicleMetadata.isRegoRequestError,
    });
    const vinInputClass = classNames({
      text: true,
      textInputVin: true,
      pc__input: true,
      "pc__input--isLoading": serviceDetailsStore.vehicleMetadata.isVinFetching,
      "input-validation-error":
        this.state.vinNumberError ||
        serviceDetailsStore.vehicleMetadata.isVinRequestError,
    });
    const intervalInputClass = classNames({
      "required text select-mac-browser": true,
      "input-validation-error":
        serviceDetailsStore.serviceIntervals.isRequestError,
    });

    return (
      <div className="servicePricing">
        <div className="row text-center">
          <div className="col-xs-12 col-md-10 col-lg-8 col-md-offset-1 col-lg-offset-2">
            <h1 className="h1 font-boxx">Service Pricing For Owners</h1>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-10 col-lg-8 col-md-offset-1 col-lg-offset-2">
            <p>
              If you own a Subaru vehicle from Model Year 2006 onwards, you can
              find the maximum price for your next standard scheduled service by
              using our Pricing Calculator.
            </p>
            <p>
              Please note prices are currently being reviewed and updated. For
              current service pricing, please contact your local Subaru Service
              Centre.
            </p>
          </div>
        </div>
        <form action="/service/service-pricing" method="post">
          <div className="row">
            <div
              id="servicePricingLookup"
              className="col-xs-12 col-md-10 col-lg-8 col-md-offset-1 col-lg-offset-2"
            >
              <fieldset className="formFieldSet">
                <div className="formField">
                  <div className="row-fluid">
                    <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-md-8 col-md-offset-2 col-lg-8 col-lg-offset-2 col-rego">
                      <InputField
                        label="What is your car registration?"
                        className={regoInputClass}
                        name="Registration"
                        id="text-input-rego"
                        onChange={(e) => {
                          this.handleRegoChange(e);
                        }}
                        isDisabled={this.state.vinNumber.length > 0}
                        onBlur={this.handleRegoBlur}
                        onKeyUp={(e) => {
                          this.handleRegoKeyUp(e);
                        }}
                        isValidationError={this.state.regoNumberError}
                        validationErrorMessage={
                          this.state.regoNumberErrorString
                        }
                        isRequestError={
                          serviceDetailsStore.vehicleMetadata.isRegoRequestError
                        }
                        requestErrorMessage="Sorry, we don’t recognise the registration number you have entered."
                      />
                    </div>
                  </div>
                </div>
              </fieldset>
              <fieldset className="formFieldSet">
                <div className="formField">
                  <div className="row-fluid">
                    <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-md-8 col-md-offset-2 col-lg-8 col-lg-offset-2 text-center">
                      <div className="or-divide">OR</div>
                    </div>
                  </div>
                </div>
              </fieldset>
              <fieldset className="formFieldSet">
                <div className="formField">
                  <div className="row-fluid">
                    <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-md-8 col-md-offset-2 col-lg-8 col-lg-offset-2">
                      <InputField
                        label="Vehicle Identification Number (VIN)"
                        className={vinInputClass}
                        name="Vin"
                        id="text-input-vin"
                        onChange={(e) => {
                          this.handleVINChange(e);
                        }}
                        isDisabled={this.state.regoNumber.length > 0}
                        onBlur={this.handleVinBlur}
                        onKeyUp={(e) => {
                          this.handleVinKeyUp(e);
                        }}
                        isValidationError={this.state.vinNumberError}
                        validationErrorMessage={this.state.vinNumberErrorString}
                        isRequestError={
                          serviceDetailsStore.vehicleMetadata.isVinRequestError
                        }
                        requestErrorMessage="Sorry, we don’t recognise the vin you have entered."
                      />
                    </div>
                  </div>
                </div>
              </fieldset>
              <fieldset className="formFieldSet">
                <div className="formField">
                  <div className="row-fluid">
                    <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-md-8 col-md-offset-2 col-lg-8 col-lg-offset-2">
                      <div className="field-spacing">
                        <label className="fieldLabel">
                          Select a Service Interval
                        </label>
                        <select
                          className={intervalInputClass}
                          id="ServiceInterval"
                          name="ServiceInterval"
                          aria-required="true"
                          onChange={(e) => {
                            this.handleIntervalChange(e);
                          }}
                          disabled={
                            serviceDetailsStore.serviceIntervals.length <= 0
                          }
                        >
                          <option>{"- Select Interval -"}</option>
                          {serviceDetailsStore.serviceIntervals.length > 0 ? (
                            serviceDetailsStore.serviceIntervals.response.map(
                              (item, i) => {
                                const displayitem = `${parseInt(
                                  item.operationDistance
                                ).toLocaleString()} km/${
                                  item.operationMonths
                                } months`;
                                return (
                                  <option
                                    value={item.groupVarId}
                                    key={`option_${i}`}
                                  >
                                    {displayitem}
                                  </option>
                                );
                              }
                            )
                          ) : (
                            <option>{"- Select Interval -"}</option>
                          )}
                        </select>
                        {serviceDetailsStore.serviceIntervals
                          .isRequestError && (
                          <span className="field-validation-error">
                            <span>
                              Sorry, we cannot get the service interval details
                              for this vehicle.
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
              <div className="row">
                <div className="col-xs-12 recall-recaptcha-wrapper">
                  <div
                    className="g-recaptcha"
                    data-sitekey={(window as any).ReCaptchaSiteKey || "6LcD-CQjAAAAAP6Ey2v2uT1Tb1D3OEOiFPLS0Zr1"}
                    data-expired-callback="recaptchaExpired"
                    data-callback="validateRecallRecaptcha"
                  />
                  {recaptchaServerFailed && (
                    <p className="recaptcha-error">
                      Failed validating Google ReCaptcha. Please try again.
                    </p>
                  )}
                </div>
              </div>
              <div className="col-xs-12 text-center">
                <p>&nbsp;</p>
                <p>
                  <input
                    id="service-price-lookup"
                    type="submit"
                    className="btn"
                    value="Find Service Price"
                    onClick={this.submitForm}
                    disabled={
                      serviceDetailsStore.vehicleMetadata.isRegoRequestError ||
                      serviceDetailsStore.vehicleMetadata.isVinRequestError ||
                      this.state.submitDisabled ||
                      !this.state.isVerified
                    }
                  />
                </p>
              </div>
              <Loader
                isLoading={serviceDetailsStore.operationDetails.isFetching}
                isPositionRelative={true}
              />
            </div>
          </div>
        </form>
        <div className="row">
          <p>&nbsp;</p>
          <div className="container">
            <ServiceDetails />
          </div>
        </div>
      </div>
    );
  }
}

export default ServicePricingForm;
